import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CartService } from '../../../services/cart.service';
import {
  OrderService,
  CreateOrderRequest,
} from '../../../services/order.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import {
  CartValidationService,
  ValidatedProduct,
} from '../../../services/cart-validation.service';
import { NotificationService } from '../../../services/notification.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { UserService, UserProfile } from '../../../services/user.service';
import { UiService } from '../../../services/ui.service';

// Modelo enriquecido para la vista
interface EnrichedCartItem extends Omit<ValidatedProduct, 'id'> {
  id: string;
  quantity: number;
}

import { GalaxyButtonDirective } from '../../../directives/galaxy-button.directive';
import { GalaxyRippleDirective } from '../../../directives/galaxy-ripple.directive';
import { GalaxyCardComponent } from '@app/components/ui/galaxy-card/galaxy-card.component';
import { InteractiveCrystalDirective } from '@app/directives/interactive-crystal.directive';
import { AnimateStaggerDirective } from '@app/directives/animate-stagger.directive';
import { GalaxySkeletonComponent } from '@app/components/ui/galaxy-skeleton/galaxy-skeleton.component';

@Component({
  selector: 'app-sumary-order',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    GalaxyButtonDirective,
    GalaxyRippleDirective,
    GalaxyCardComponent,
    InteractiveCrystalDirective,
    AnimateStaggerDirective,
    GalaxySkeletonComponent,
  ],
  templateUrl: './sumary-order.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sumary-order.component.css'],
})
export class SumaryOrderComponent implements OnInit {
  enrichedCartItems: EnrichedCartItem[] = [];
  currentUserProfile: UserProfile | null = null;
  totalOrder: number = 0;
  isProcessing: boolean = false;
  isLoading: boolean = true;
  canProceedToPayment: boolean = false;
  selectedPaymentMethod: 'PAYPAL' | 'CASH_ON_DELIVERY' = 'PAYPAL';
  
  // Datos de envío para usuarios nuevos/Google
  shippingAddress: string = '';
  cellphone: string = '';

  constructor(
    private cartService: CartService,
    private cartValidationService: CartValidationService,
    private router: Router,
    private orderService: OrderService,
    private sessionStorage: SessionStorageService,
    private notificationService: NotificationService,
    private userService: UserService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    const token = this.sessionStorage.getItem('token');
    
    if (token) {
      this.userService.getProfile().subscribe({
        next: (profile) => {
          this.currentUserProfile = profile;
          this.shippingAddress = profile.address || '';
          this.cellphone = profile.cellphone || '';
          this.validateAndLoadCart();
        },
        error: (err) => {
          console.error('Error fetching user profile', err);
          this.isLoading = false;
        },
      });
    } else {
      this.currentUserProfile = null;
      this.validateAndLoadCart();
    }
  }

  validateAndLoadCart(): void {
    const cartState = this.cartService.getCartState();

    if (cartState.length === 0) {
      this.isLoading = false;
      this.enrichedCartItems = [];
      return;
    }

    this.cartValidationService
      .validateCartItems(cartState)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (validatedProducts) => {
          const validatedMap = new Map(validatedProducts.map((p) => [`${p.productId}_${p.formatKey}`, p]));
          this.enrichedCartItems = cartState.map((item) => {
            const key = `${item.productId}_${item.formatKey}`;
            const productDetails = validatedMap.get(key);
            return {
              id: key,
              productId: item.productId,
              formatKey: item.formatKey,
              formatName: productDetails ? productDetails.formatName : item.formatKey,
              name: productDetails
                ? productDetails.name
                : 'Producto no disponible',
              price: productDetails ? productDetails.price : 0,
              imageUrl: productDetails ? productDetails.imageUrl : '',
              active: productDetails ? productDetails.active : false,
              quantity: item.quantity,
            };
          });
          this.calculateTotals();
        },
        error: (err) => {
          console.error('Error validating cart', err);
          this.enrichedCartItems = [];
        },
      });
  }

  calculateTotals(): void {
    this.totalOrder = this.enrichedCartItems
      .filter((item) => item.active)
      .reduce((acc, item) => acc + item.price * item.quantity, 0);

    this.canProceedToPayment =
      this.enrichedCartItems.length > 0 &&
      this.enrichedCartItems.every((item) => item.active);
  }

  deleteItemCart(cartKey: string): void {
    this.cartService.deleteItem(cartKey);
    this.validateAndLoadCart();
  }

  increaseQuantity(item: EnrichedCartItem): void {
    if (!item.active) return;
    const newQuantity = item.quantity + 1;
    this.cartService.updateItemQuantity(item.id, newQuantity);
    item.quantity = newQuantity;
    this.calculateTotals();
  }

  decreaseQuantity(item: EnrichedCartItem): void {
    if (!item.active) return;
    const newQuantity = item.quantity - 1;
    if (newQuantity <= 0) {
      this.deleteItemCart(item.id);
    } else {
      this.cartService.updateItemQuantity(item.id, newQuantity);
      item.quantity = newQuantity;
      this.calculateTotals();
    }
  }

  proceedToPay(): void {
    const token = this.sessionStorage.getItem('token');
    if (!token) {
      this.uiService.openAuthModal();
      return;
    }

    if (!this.shippingAddress || !this.shippingAddress.trim() || !this.cellphone || !this.cellphone.trim()) {
      this.notificationService.showWarning('Datos Incompletos', 'Por favor, ingresa tu dirección y teléfono para el envío.');
      return;
    }

    if (!this.canProceedToPayment || this.isProcessing) return;
    this.isProcessing = true;

    const orderItems = this.enrichedCartItems
      .filter((item) => item.active)
      .map((item) => ({ productId: item.productId, formatKey: item.formatKey, quantity: item.quantity }));

    const request: CreateOrderRequest = {
      items: orderItems,
      paymentMethod: this.selectedPaymentMethod,
      shippingAddress: this.shippingAddress.trim(),
      cellphone: this.cellphone.trim(),
    };

    this.orderService.createOrder(request).subscribe({
      next: (response) => {
        if (response.approvalUrl) {
          this.sessionStorage.setItem('orderId', response.orderId);
          window.location.href = response.approvalUrl;
        } else {
          this.cartService.clearCart();
          this.router.navigate(['/payment-success'], {
            queryParams: {
              payment_status: 'success',
              orderId: response.orderId,
            },
          });
        }
      },
      error: (error) => {
        console.error('Error creating order', error);
        this.notificationService.showError(
          'Error en la Orden',
          error.error?.message ||
            'Un producto en tu carrito ya no está disponible.',
        );
        this.isProcessing = false;
        this.validateAndLoadCart();
      },
    });
  }
}
